import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router";

interface AuthFormProps {
  mode: "login" | "signup";
  onModeChange: (mode: "login" | "signup") => void;
  onClose: () => void;
}

const AuthForm: React.FC<AuthFormProps> = ({ mode, onModeChange, onClose }) => {
  const { signIn, signUp } = useAuth();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    console.log(`Starting ${mode} process for:`, formData.email);

    try {
      if (mode === "signup") {
        console.log("Attempting signup...");
        const { error } = await signUp(
          formData.email,
          formData.password,
          formData.name,
        );
        if (error) {
          console.error("Signup error:", error);
          setError(error.message);
        } else {
          console.log("Signup successful");
          setSuccess("Account created successfully! You can now sign in.");
          // Clear form
          setFormData({ name: "", email: "", password: "" });
          // Switch to login mode
          setTimeout(() => {
            onModeChange("login");
            setSuccess(null);
          }, 2000);
        }
      } else {
        console.log("Attempting sign in...");
        const { error } = await signIn(formData.email, formData.password);
        if (error) {
          console.error("Sign in error:", error);
          setError(error.message);
        } else {
          console.log("Sign in successful");
          setSuccess("Successfully signed in! Redirecting...");
          // Close modal after short delay
          setTimeout(() => {
            onClose();
            setSuccess(null);
            navigate("/virtual-binder");
          }, 1000);
        }
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
          <p className="text-red-100 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-4">
          <p className="text-green-100 text-sm">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 mt-6">
        {mode === "signup" && (
          <div className="space-y-2">
            <Label htmlFor="name" className="text-white/90 font-inter">
              Name
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className="bg-deep-indigo/20 border-holo-blue/30 text-white placeholder:text-white/50 focus:border-electric-yellow"
              placeholder="Enter your name"
              required
              disabled={loading}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email" className="text-white/90 font-inter">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            className="bg-deep-indigo/20 border-holo-blue/30 text-white placeholder:text-white/50 focus:border-electric-yellow"
            placeholder="Enter your email"
            required
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-white/90 font-inter">
            Password
          </Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            className="bg-deep-indigo/20 border-holo-blue/30 text-white placeholder:text-white/50 focus:border-electric-yellow"
            placeholder="Enter your password"
            required
            disabled={loading}
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-holo-blue to-electric-yellow hover:from-holo-blue/90 hover:to-electric-yellow/90 text-deep-indigo font-bold font-inter text-lg py-3 rounded-lg mt-6"
        >
          {loading
            ? mode === "signup"
              ? "Creating Account..."
              : "Signing In..."
            : mode === "signup"
              ? "Sign Up"
              : "Sign In"}
        </Button>
      </form>

      {mode === "login" && (
        <div className="text-center mt-4">
          <button
            onClick={() => navigate("/forgot-password")}
            className="text-white/70 hover:text-white text-sm underline"
          >
            Forgot your password?
          </button>
        </div>
      )}
    </>
  );
};

export default AuthForm;
